import { Panel } from "../../../components/panels/Panel";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  SignalSlashIcon,
  XMarkIcon
} from "@heroicons/react/20/solid";
import { createCsvData, downloadCsvData } from "../../../helpers/file/file-helper";
import { InputSelect } from "../../../components/forms/inputs/InputSelect";
import { createDateRange, saveLayoutData } from "../../../services/ergonomics/ergonomics-service";
import { useOrgData } from "../../../contexts/OrgDataContext";
import { useErgonomicsContext } from "../../../contexts/ErgonomicsContext";
import { cn, Switch } from "@heroui/react";
import { DateRangePicker } from "@heroui/date-picker";
import { useState, useRef, useEffect } from "react";
import { calendarDateTimetoDate, formatDateTimeFromTimestamp } from "../../../helpers/date/date-helper";
import { Checkbox } from "../../../components/forms/Checkbox";
import { ErgonomicsMiniPanel } from "./ErgonomicsMiniPanel";


const ErgonomicsSettingsPanel = () => {

  const { workstations, workstationSelected, setWorkstationSelected } = useOrgData();
  const { isLiveMode, showRight, setShowRight, selectedDateRange,
    setSelectedDateRange, setZoomDateRange, layoutData, setLayoutData } = useErgonomicsContext();

  const initialRange = useRef(selectedDateRange);

  const [range, setRange] = useState({
    start: formatDateTimeFromTimestamp(selectedDateRange.start),
    end: formatDateTimeFromTimestamp(selectedDateRange.stop),
  });

  // Sync with selectedDateRange changes
  useEffect(() => {
    setRange({
      start: formatDateTimeFromTimestamp(selectedDateRange.start),
      end: formatDateTimeFromTimestamp(selectedDateRange.stop),
    });
  }, [selectedDateRange]);

  const validateRange = () => {
    setZoomDateRange(null);
    const newLayoutData = { ...layoutData, zoomDateRange: null };
    setLayoutData(newLayoutData);
    saveLayoutData(newLayoutData);
    const startTimestamp = calendarDateTimetoDate(range.start).getTime();
    const stopTimestamp = calendarDateTimetoDate(range.end).getTime();
    setSelectedDateRange(createDateRange(startTimestamp, stopTimestamp));
  };

  const resetRange = () => {
    setZoomDateRange(null);
    const newLayoutData = { ...layoutData, zoomDateRange: null };
    setLayoutData(newLayoutData);
    saveLayoutData(newLayoutData);
    const startTimestamp = calendarDateTimetoDate(formatDateTimeFromTimestamp(initialRange.current.start)).getTime();
    const stopTimestamp = calendarDateTimetoDate(formatDateTimeFromTimestamp(initialRange.current.stop)).getTime();
    setRange({
      start: formatDateTimeFromTimestamp(initialRange.current.start),
      end: formatDateTimeFromTimestamp(initialRange.current.stop)
    });
    setSelectedDateRange(createDateRange(startTimestamp, stopTimestamp));
  };

  const exportData = () => {
    // Not implemented yet
    // let csvData = [
    //   ['Metrique', 'Valeur maximale', 'Valeur moyenne'],
    //   ['Position de la nuque', maxValues[1], Math.round(meanValues[1] * 10) / 10],
    //   ['Position du tronc', maxValues[2], Math.round(meanValues[2] * 10) / 10],
    //   ['Position des jambes', maxValues[3], Math.round(meanValues[3] * 10) / 10],
    //   ['Score de posture A', maxValues[4], Math.round(meanValues[4] * 10) / 10],
    //   ['Position de l\'epaule', maxValues[7], Math.round(meanValues[7] * 10) / 10],
    //   ['Position du coude', maxValues[8], Math.round(meanValues[8] * 10) / 10],
    //   ['Score de posture B', maxValues[10], Math.round(meanValues[10] * 10) / 10],
    //   ['Score de posture C', maxValues[13], Math.round(meanValues[13] * 10) / 10],
    // ];
    // downloadCsvData(createCsvData(csvData), 'REBA_scores');
  };

  const enableLiveMode = () => {
    const u = new URL(location.href);
    u.searchParams.set('live', '1');
    location.replace(u);
  };

  const disableLiveMode = () => {
    const u = new URL(location.href);
    u.searchParams.delete('live');
    location.replace(u);
  };

  const SHOW_BUTTONS = false;
  const SHOW_LIVE_MODE = false;

  return (
    <Panel title="PARAMETRAGE" height="h-3/6">
      {
        SHOW_BUTTONS &&
        <div className="flex justify-start items-center gap-4 mb-4">
          <button className="text-white bg-perception-blue px-4 py-2 text-sm flex items-center rounded-md" onClick={null}>
            <ArrowUpTrayIcon aria-hidden="true" className="size-4 mr-2" /> Importer chronique d'activité
          </button>

          <button className="text-white bg-perception-blue px-4 py-2 text-sm flex items-center rounded-md" onClick={exportData}>
            <ArrowDownTrayIcon aria-hidden="true" className="size-4 mr-2" /> Exporter
          </button>
        </div>
      }
      {
        SHOW_LIVE_MODE &&
        <div className="mb-4">
          {
            !isLiveMode ?
              <button className="text-white bg-perception-blue px-4 py-2 text-sm flex items-center rounded-md" onClick={enableLiveMode}>
                <SignalIcon aria-hidden="true" className="size-4 mr-2" /> Activer le mode temps réel
              </button>
              :
              <button className="text-white bg-perception-blue px-4 py-2 text-sm flex items-center rounded-md" onClick={disableLiveMode}>
                <SignalSlashIcon aria-hidden="true" className="size-4 mr-2" /> Arrêter le mode temps réel
              </button>
          }
        </div>
      }

      {/* Workstation selection */}
      <ErgonomicsMiniPanel title="POSTE OBSERVE">
        <InputSelect
          items={workstations}
          onChange={setWorkstationSelected}
          itemToString={(item) => item?.name}
          defaultValue={workstationSelected}
        />
      </ErgonomicsMiniPanel>

      {/* Date selection */}
      {
        !isLiveMode &&
        <ErgonomicsMiniPanel title="PERIODE OBSERVEE">
          <DateRangePicker
            classNames={{
              base: 'p-0 gap-0',
              inputWrapper: 'w-full bg-transparent hover:bg-transparent focus-within:hover:bg-transparent text-white cursor-default p-0 min-h-0 h-auto',
              innerWrapper: 'flex flex-wrap',
              label: 'text-white text-sm w-auto',
              input: 'bg-perception-gray-700 rounded-md px-2 w-auto'
            }}
            value={{
              start: range.start,
              end: range.end,
            }}
            className="w-full max-w-full" radius="none" granularity="minute" label={null}
            onChange={({ start, end }) => setRange({ start, end })}
          />
          <div className="mt-11 flex gap-2">
            <button className="text-xs text-white bg-perception-gray-700 py-1 px-2 flex items-center rounded-md" onClick={resetRange}>
              <XMarkIcon aria-hidden="true" className="size-4 mr-1" /> Reset
            </button>
            <button className="text-xs text-white bg-perception-blue py-1 px-2 flex items-center rounded-md" onClick={validateRange}>
              Charger
            </button>
          </div>
        </ErgonomicsMiniPanel>
      }

      {/* Left/right selection */}
      <ErgonomicsMiniPanel title="MEMBRES OBSERVES">
        <div className="flex gap-4 items-center justify-center text-sm">
          Gauche
          <Switch isSelected={showRight} onValueChange={setShowRight} classNames={{
            wrapper: "p-0 h-4 overflow-visible group-data-[selected=true]:bg-perception-blue",
            thumb: cn(
              "w-6 h-6 border-2 shadow-lg",
              "group-data-[hover=true]:border-perception-blue",
              //selected
              "group-data-[selected=true]:ms-6",
              // pressed
              "group-data-[pressed=true]:w-7",
              "group-data-pressed:group-data-selected:ms-4",
            ),
          }}/>
          Droite
        </div>
      </ErgonomicsMiniPanel>
    </Panel>
  );
};

const ErgonomicsPhasesPanel = () => {

  const { isLiveMode, checkboxes, setCheckboxes, filters, flags } = useErgonomicsContext();

  const handleFilterChange = (event) => {
    const { id, checked } = event.target;
    setCheckboxes((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  if (isLiveMode) return null;

  return (
    <Panel title="CHRONIQUE D'ACTIVITES" height="h-2/6">
      {/* Phase selection */}
      {
        !isLiveMode &&
        <ErgonomicsMiniPanel title="SELECTION DES PHASES">
          <div className="flex flex-wrap gap-x-4">
            {
              Object.entries(checkboxes).filter(([key, value]) => key !== 'workstation_id').map(([key, value], index) => (
                <Checkbox key={index} label={filters.data[index].display_text} id={key} checked={value} onChange={handleFilterChange} />
              ))
            }
          </div>

          {
            flags.filter((flag) => flag).length === 0 &&
            <div className="rounded-md bg-perception-black-500 p-2 mt-4">
              <div className="flex">
                <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-400">Aucune donnée pour les critères sélectionnés.</h3>
                </div>
              </div>
            </div>
          }
        </ErgonomicsMiniPanel>
      }
    </Panel>
  );
};

export const ErgonomicsLeftPanel = () => {

  const { isEditingLayout, setIsEditingLayout } = useErgonomicsContext();

  return (
    <div className="h-full flex flex-col justify-between">
      <ErgonomicsSettingsPanel/>
      <ErgonomicsPhasesPanel/>

      <Panel title="PARAMETRAGE DE L'OUTIL" height="h-1/12">
        <div className="flex gap-4 items-center text-sm">
          Déplacement des tuiles
          <Switch isSelected={isEditingLayout} onValueChange={setIsEditingLayout} classNames={{
            wrapper: "p-0 h-4 overflow-visible group-data-[selected=true]:bg-perception-blue",
            thumb: cn(
              "w-6 h-6 border-2 shadow-lg",
              "group-data-[hover=true]:border-perception-blue",
              //selected
              "group-data-[selected=true]:ms-6",
              // pressed
              "group-data-[pressed=true]:w-7",
              "group-data-pressed:group-data-selected:ms-4",
            ),
          }}/>
        </div>
      </Panel>
    </div>
  );
};